import { getGLFormatTypeAlignment } from "./PlotComponent";
import { WGLProgram, WGLTexture } from "autumn-wgl";
const billboard_vertex_shader_src = `uniform mat4 u_matrix;
uniform int u_offset;

attribute vec3 a_pos;    
attribute vec2 a_tex_coord;
uniform lowp float u_bb_size;
uniform lowp float u_map_aspect;
uniform lowp float u_zoom;
uniform highp float u_map_bearing;
uniform lowp float u_bb_width;  
uniform lowp float u_bb_height; 
uniform highp float u_bb_mag_bin_size;

uniform highp float u_bb_mag_wrap;

uniform sampler2D u_u_sampler;
uniform sampler2D u_v_sampler;

varying highp vec2 v_tex_coord;

mat4 scalingMatrix(float x_scale, float y_scale, float z_scale) {
    return mat4(x_scale, 0.0,     0.0,     0.0,
                0.0,     y_scale, 0.0,     0.0,
                0.0,     0.0,     z_scale, 0.0,
                0.0,     0.0,     0.0,     1.0);
}

mat4 rotationZMatrix(float angle) {
    float s = sin(angle);
    float c = cos(angle);

    return mat4( c,  s,  0., 0.,
                -s,  c,  0., 0.,
                 0., 0., 1., 0.,
                 0., 0., 0., 1.);
}

mat4 rotationXMatrix(float angle) {
    float s = sin(angle);
    float c = cos(angle);

    return mat4( 1.,  0., 0., 0.,
                 0.,  c,  s,  0.,
                 0., -s,  c,  0.,
                 0.,  0., 0., 1.);
}

void main() {
    float globe_width = 1.;
    vec2 globe_offset = vec2(globe_width * float(u_offset), 0.);

    vec4 pivot_pos = u_matrix * vec4(a_pos.xy + globe_offset, 0.0, 1.0);
    highp float zoom_corner = a_pos.z;
    lowp float min_zoom = floor(zoom_corner / 4.0);
    lowp float corner = mod(zoom_corner, 4.0);

    highp float u = texture2D(u_u_sampler, a_tex_coord).r;
    highp float v = texture2D(u_v_sampler, a_tex_coord).r;

    lowp float bb_aspect = u_bb_width / u_bb_height;
    lowp float ang = (abs(u) < 1e-6 && abs(v) < 1e-6) ? 0. : atan(v, u) - 3.141592654 / 2.0;
    highp float mag = length(vec2(u, v));
    mag = floor(mag / u_bb_mag_bin_size + 0.5) * u_bb_mag_bin_size;

    vec4 offset = vec4(0.0, 0.0, 0.0, 0.0);
    vec2 texcoord = vec2(0.0, 0.0);
    
    if (u_zoom >= min_zoom) {
        vec2 tex_loc = vec2(mod(mag, u_bb_mag_wrap) / u_bb_mag_bin_size * u_bb_width, floor(mag / u_bb_mag_wrap) * u_bb_height);

        if (corner < 0.5) {
            offset = vec4(-u_bb_size, u_bb_size, 0., 0.);
            texcoord = tex_loc;
        }
        else if (corner < 1.5) {
            offset = vec4(u_bb_size, u_bb_size, 0., 0.);
            texcoord = tex_loc + vec2(u_bb_width, 0.0);
        }
        else if (corner < 2.5) {
            offset = vec4(-u_bb_size, -u_bb_size * (2. / bb_aspect - 1.), 0., 0.);
            texcoord = tex_loc + vec2(0.0, u_bb_height);
        }
        else if (corner < 3.5) {
            offset = vec4(u_bb_size, -u_bb_size * (2. / bb_aspect - 1.), 0., 0.);
            texcoord = tex_loc + vec2(u_bb_width, u_bb_height);
        }

        mat4 barb_rotation = rotationZMatrix(ang + radians(u_map_bearing));
        mat4 map_stretch_matrix = scalingMatrix(1.0, 1. / u_map_aspect, 1.0);
        offset = map_stretch_matrix * barb_rotation * offset;
    }

    gl_Position = pivot_pos + offset;
    v_tex_coord = texcoord;
}`
const billboard_fragment_shader_src = `varying highp vec2 v_tex_coord;

uniform sampler2D u_sampler;
uniform lowp vec3 u_bb_color;

void main() {
    lowp vec4 tex_color = texture2D(u_sampler, v_tex_coord);
    gl_FragColor = vec4(u_bb_color, tex_color.a); 
}`
class BillboardCollection {
    constructor(gl, field, thin_fac, max_zoom, billboard_image, billboard_spec, billboard_color, billboard_size_mult) {
        this.spec = billboard_spec;
        this.color = billboard_color;
        this.size_multiplier = billboard_size_mult;
        this.program = new WGLProgram(gl, billboard_vertex_shader_src, billboard_fragment_shader_src);
        this.vertices = null;
        this.texcoords = null;
        const n_density_tiers = Math.log2(thin_fac);
        const n_inaccessible_tiers = Math.max(n_density_tiers + 1 - max_zoom, 0);
        const trim_inaccessible = Math.pow(2, n_inaccessible_tiers);
        const earth_relative = field.getThinnedField(trim_inaccessible, trim_inaccessible).toEarthRelative();
        const u_thin = earth_relative.u;
        const v_thin = earth_relative.v;
        (async () => {
            const { vertices, texcoords } = await earth_relative.grid.getWGLBillboardBuffers(gl, thin_fac / trim_inaccessible, max_zoom);
            this.vertices = vertices;
            this.texcoords = texcoords;
        })();
        const { format, type, row_alignment } = getGLFormatTypeAlignment(gl, u_thin.isFloat16());
        const u_image = { 'format': format, 'type': type,
            'width': u_thin.grid.ni, 'height': u_thin.grid.nj, 'image': u_thin.getTextureData(),
            'mag_filter': gl.NEAREST, 'row_alignment': row_alignment,
        };
        const v_image = { 'format': format, 'type': type,
            'width': v_thin.grid.ni, 'height': v_thin.grid.nj, 'image': v_thin.getTextureData(),
            'mag_filter': gl.NEAREST, 'row_alignment': row_alignment,
        };
        this.texture = new WGLTexture(gl, billboard_image);
        this.u_texture = new WGLTexture(gl, u_image);
        this.v_texture = new WGLTexture(gl, v_image);
    }
    render(gl, matrix, [map_width, map_height], map_zoom, map_bearing, map_pitch) {
        if (this.vertices === null || this.texcoords === null)
            return;
        if (matrix instanceof Float32Array)
            matrix = [...matrix];
        const bb_size = this.spec.BB_HEIGHT * (map_height / map_width) * this.size_multiplier;
        const bb_width = this.spec.BB_WIDTH / this.spec.BB_TEX_WIDTH;
        const bb_height = this.spec.BB_HEIGHT / this.spec.BB_TEX_HEIGHT;
        this.program.use({ 'a_pos': this.vertices, 'a_tex_coord': this.texcoords }, { 'u_bb_size': bb_size, 'u_bb_width': bb_width, 'u_bb_height': bb_height,
            'u_bb_mag_bin_size': this.spec.BB_MAG_BIN_SIZE, 'u_bb_mag_wrap': this.spec.BB_MAG_WRAP, 'u_offset': 0,
            'u_bb_color': this.color, 'u_matrix': matrix, 'u_map_aspect': map_height / map_width, 'u_zoom': map_zoom, 'u_map_bearing': map_bearing }, { 'u_sampler': this.texture, 'u_u_sampler': this.u_texture, 'u_v_sampler': this.v_texture });
        gl.enable(gl.BLEND);
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        this.program.draw();
        this.program.setUniforms({ 'u_offset': -2 });
        this.program.draw();
        this.program.setUniforms({ 'u_offset': -1 });
        this.program.draw();
        this.program.setUniforms({ 'u_offset': 1 });
        this.program.draw();
    }
}
export { BillboardCollection };
